// Stellarium Web - Copyright (c) 2022 - Stellarium Labs SRL
//
// This program is licensed under the terms of the GNU AGPL v3, or
// alternatively under a commercial licence.
//
// The terms of the AGPL v3 license can be found in the main directory of this
// repository.

<template>

<v-app>
  <v-navigation-drawer v-model="nav" app stateless width="300">
    <v-layout column fill-height>
      <v-list dense>
        <template v-for="(item,i) in menuItems">
          <template v-if="$store.state[item.store_show_menu_item] === false"></template>
          <v-subheader v-else-if="item.header" v-text="item.header" class="grey--text text--darken-1" :key="i"/>
          <v-divider class="divider_menu" v-else-if="item.divider" :key="i"/>
          <v-list-item v-else-if="item.switch" @click.stop="toggleStoreValue(item.store_var_name)" :key="i">
            <v-list-item-action>
              <v-switch value :input-value="getStoreValue(item.store_var_name)" label=""></v-switch>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <template v-else>
            <v-list-item v-if='item.link' target="_blank" rel="noopener" :href='item.link' :key="i">
              <v-list-item-icon><v-icon>{{ item.icon }}</v-icon></v-list-item-icon>
              <v-list-item-title v-text="item.title"/>
              <v-icon disabled>mdi-open-in-new</v-icon>
            </v-list-item>
            <v-list-item v-else-if='item.footer===undefined' @click.stop="toggleStoreValue(item.store_var_name)" :key="i">
              <v-list-item-icon><v-icon>{{ item.icon }}</v-icon></v-list-item-icon>
              <v-list-item-title v-text="item.title"/>
            </v-list-item>
          </template>
        </template>
      </v-list>
      <template v-for="(item,i) in menuComponents">
        <component :is="item" :key="i"></component>
      </template>
      <v-spacer></v-spacer>
      <v-list dense>
        <v-divider class="divider_menu"/>
        <template v-for="(item,i) in menuItems">
          <v-list-item v-if='item.footer' @click.stop="toggleStoreValue(item.store_var_name)" :key="i">
            <v-list-item-icon><v-icon>{{ item.icon }}</v-icon></v-list-item-icon>
            <v-list-item-title v-text="item.title"/>
          </v-list-item>
        </template>
      </v-list>
    </v-layout>
  </v-navigation-drawer>

  <v-main>
    <v-container class="fill-height" fluid style="padding: 0">
      <div id="stel" v-bind:class="{ right_panel: $store.state.showSidePanel }">
        <div style="position: relative; width: 100%; height: 100%">
          <component v-bind:is="guiComponent"></component>
          <canvas id="stel-canvas" ref='stelCanvas'></canvas>
        </div>
      </div>
    </v-container>
    <!-- [guanxing patch] 出生天象信息条（M-SW4）：提交出生信息后展示
         「日期 · 地点 · 月相照亮」，fixed 定位浮在星空上方，可关闭；
         M-SW13 起加定时自动消失（原 10s，2026-10-13 缩短为 2s，脚本置 birthBanner=false）。
         M-SW44：按用户需求「换成深色半透明」——去掉 type="info"（同时去掉
         Vuetify 蓝色底与左侧图标），改由 .birth-banner 自绘深色半透明底。 -->
    <v-alert v-if="birthInfo" v-model="birthBanner" dense dismissible class="birth-banner">
      {{ birthInfo }}
    </v-alert>
    <!-- [guanxing patch] M-SW56（用户需求「点击星命里的按钮后 锁定到星座上之后
         2 秒后 屏幕中央弹出一个提示 淡入淡出 如果被地景遮挡 下方 视图设置>关闭
         地景 显示 3 秒」）：锁定目标被地景遮挡时于屏幕中央淡入的轻提示。
         纯展示、pointer-events:none 不挡操作；可见期 5s（M-SW56b：用户「提示时长
         延长 2 秒」，由 3s 延为 5s），前后各 0.3s 过渡（详见 scheduleLockHint）。
         只在遮挡且地景开启时出现（决策人 2026-10-14 定案：未遮挡或地景已关闭不提示）。 -->
    <transition name="gx-hint">
      <div v-if="lockHint" class="lock-hint">
        <div class="lock-hint-main">镜头已落在「{{ lockHintName }}」上</div>
        <div class="lock-hint-sub">它被地景挡住了 · 视图设置 &gt; 关闭地景</div>
      </div>
    </transition>
    <!-- [guanxing patch] 出生天象首页（M-SW4）：首访覆盖层（URL 无 date 时显示，
         提交后冻结引擎在该时刻；带 date 直达=分享/刷新重现，不弹）。
         [guanxing patch] M-SW21：包 transition 淡出离场（0.6s）；落地层 z-index
         已降至 150（低于 v-dialog 202），淡出期间不遮挡同步弹出的本命星命弹窗。 -->
    <transition name="gx-fade">
      <birth-overlay v-if="showBirthOverlay" @submitted="onBirthSubmitted" @skipped="onBirthSkipped"/>
    </transition>
    <!-- [guanxing patch] M-SW15：本命星命弹窗（用户需求）：提交出生信息或 birth=1
         直达时弹出，展示 星座/星次/本命宿/本命星君 四项（M-SW19 起四行整行可点
         直接跳转：星座/星次行 → 西方星座聚焦，本命宿/本命星君行 → 二十八宿聚焦）。
         点遮罩/✕ 关闭。 -->
    <birth-fate-dialog v-if="birthFate" v-model="showFateDialog" :fate="birthFate"
                       @view-zodiac="viewBirthZodiac" @view-mansion="viewBirthMansion"
                       @view-lord="viewBirthLord" @view-xingci="viewBirthXingci" @view-solar="viewBirthSolar"/>
    <!-- [guanxing patch] M-SW15b：左上角星命入口（用户需求）：弹窗关闭后仍可
         随时重开本命星命弹窗；birthFate 为空（跳过出生输入）时不显示。 -->
    <button v-if="birthFate" type="button" class="fate-entry"
            @click="showFateDialog = true">星命</button>
  </v-main>

</v-app>

</template>

<script>

import _ from 'lodash'
import Gui from '@/components/gui.vue'
import GuiLoader from '@/components/gui-loader.vue'
import BirthOverlay from '@/components/birth-overlay.vue'
// [guanxing patch] M-SW15：本命星命弹窗 + 出生时刻太阳/月亮黄经计算
// （astronomy-engine 为 apps/web 既有依赖，M-SW9 起前端重引入）。
import BirthFateDialog from '@/components/birth-fate-dialog.vue'
import * as Astronomy from 'astronomy-engine'
import { lunarMansionOf, xingciOf } from '@/assets/birth-fate.js'
import swh from '@/assets/sw_helpers.js'
import Moment from 'moment'

// [guanxing patch] M-SW5：占星十二宫日期表（大众认知口径，非天文太阳实际
// 所在星座）。code = 引擎星座三字码，必须与 skydata/skycultures/western/
// index.json 的 id 字面一致（首字母大写，如 Cnc——巨蟹座惯称 CAN 并不存在，
// core_search 按 strcasecmp 匹配 designation，码本身不存在则查不到），
// zh = 中文显示名，[m0,d0]~[m1,d1] = 民用日期段（摩羯座跨年，wrap 特判）。
const ZODIAC_SIGNS = [
  { code: 'Ari', zh: '白羊座', m0: 3, d0: 21, m1: 4, d1: 19 },
  { code: 'Tau', zh: '金牛座', m0: 4, d0: 20, m1: 5, d1: 20 },
  { code: 'Gem', zh: '双子座', m0: 5, d0: 21, m1: 6, d1: 21 },
  { code: 'Cnc', zh: '巨蟹座', m0: 6, d0: 22, m1: 7, d1: 22 },
  { code: 'Leo', zh: '狮子座', m0: 7, d0: 23, m1: 8, d1: 22 },
  { code: 'Vir', zh: '室女座', m0: 8, d0: 23, m1: 9, d1: 22 },
  { code: 'Lib', zh: '天秤座', m0: 9, d0: 23, m1: 10, d1: 23 },
  { code: 'Sco', zh: '天蝎座', m0: 10, d0: 24, m1: 11, d1: 22 },
  { code: 'Sgr', zh: '人马座', m0: 11, d0: 23, m1: 12, d1: 21 },
  { code: 'Cap', zh: '摩羯座', m0: 12, d0: 22, m1: 1, d1: 19 },
  { code: 'Aqr', zh: '宝瓶座', m0: 1, d0: 20, m1: 2, d1: 18 },
  { code: 'Psc', zh: '双鱼座', m0: 2, d0: 19, m1: 3, d1: 20 }
]

// [guanxing patch] M-SW36：聚焦地平线下的星座/宿时，地景让路的不透明度。
// 1 = 完全不透明（引擎默认），越小白越淡。取 0.35 而非 0——「明显淡下去、但仍
// 能看出地平线在哪」；完全消失正是本次要避免的观感。
const LAND_DIM_OPACITY = 0.35

export default {
  data (context) {
    return {
      menuItems: [
        { title: this.$t('View Settings'), icon: 'mdi-settings', store_var_name: 'showViewSettingsDialog', store_show_menu_item: 'showViewSettingsMenuItem' },
        { title: this.$t('Planets Tonight'), icon: 'mdi-panorama-fisheye', store_var_name: 'showPlanetsVisibilityDialog', store_show_menu_item: 'showPlanetsVisibilityMenuItem' },
        { divider: true }
      ].concat(this.getPluginsMenuItems()).concat([
        { title: this.$t('Data Credits'), footer: true, icon: 'mdi-copyright', store_var_name: 'showDataCreditsDialog' }
      ]),
      menuComponents: [].concat(this.getPluginsMenuComponents()),
      guiComponent: 'GuiLoader',
      startTimeIsSet: false,
      initDone: false,
      dataSourceInitDone: false,
      // [guanxing patch] 出生天象首页（M-SW4）：URL 无 date 时首访显示覆盖层；
      // birthInfo 非空时顶部显示信息条（birthBanner 控制用户手动关闭）。
      showBirthOverlay: !this.$route.query.date,
      birthInfo: '',
      birthBanner: false,
      // [guanxing patch] M-SW13：信息条定时消失计时器句柄（非响应式，
      // 原 10s 现 2s，仅用于重提交时重置计时）。
      birthBannerTimer: null,
      // [guanxing patch] M-SW15：本命星命弹窗状态（birthFate 非空才挂载组件）
      showFateDialog: false,
      birthFate: null,
      // [guanxing patch] M-SW56：锁定后居中轻提示（遮挡时弹）。
      // lockHint 控制显隐（v-if + gx-hint 过渡实现淡入淡出）；
      // lockHintName 是目标中文名（星座名 / 「X宿」），由触发方传入。
      lockHint: false,
      lockHintName: ''
    }
  },
  components: { Gui, GuiLoader, BirthOverlay, BirthFateDialog },
  methods: {
    getPluginsMenuItems: function () {
      let res = []
      for (const i in this.$stellariumWebPlugins()) {
        const plugin = this.$stellariumWebPlugins()[i]
        if (plugin.menuItems) {
          res = res.concat(plugin.menuItems)
        }
      }
      return res
    },
    getPluginsMenuComponents: function () {
      let res = []
      for (const i in this.$stellariumWebPlugins()) {
        const plugin = this.$stellariumWebPlugins()[i]
        if (plugin.menuComponents) {
          res = res.concat(plugin.menuComponents)
        }
      }
      return res
    },
    toggleStoreValue: function (storeVarName) {
      this.$store.commit('toggleBool', storeVarName)
    },
    // [guanxing patch] M-SW4：出生信息提交/跳过。提交后顶部显示
    // 「日期 · 地点 · 月相照亮 xx%」（月相读取失败时省略月相段），
    // 2s 定时自动消失（M-SW13 原为 10s，2026-10-13 按用户需求缩短为 2s；
    // 用户提前手动关闭时计时器置 false 无副作用）。
    onBirthSubmitted: function (info) {
      this.birthInfo = info.phasePct === null || info.phasePct === undefined
        ? info.dateText + ' · ' + info.cityText
        : info.dateText + ' · ' + info.cityText + ' · 月相照亮 ' + info.phasePct + '%'
      this.birthBanner = true
      clearTimeout(this.birthBannerTimer)
      this.birthBannerTimer = setTimeout(() => { this.birthBanner = false }, 2000)
      this.showBirthOverlay = false
      // [guanxing patch] M-SW21b：落地页期间大气被 watcher 关闭，提交进入出生
      // 天象后恢复真实观感（与 onBirthSkipped 对称；引擎 fader 平滑渐显）。
      if (this.$stel) this.$stel.core.atmosphere.visible = true
      // [guanxing patch]（用户需求「首页背景星空的星座名字不显示」）：落地页期间
      // 星座名标签被 watcher 隐藏，提交后恢复「默认打开」契约（连线本来就没关）。
      if (this.$stel) this.$stel.core.constellations.labels_visible = true
      // [guanxing patch] M-SW15：先弹本命星命弹窗（用户需求），聚焦动作交给
      // 弹窗的两个按钮（viewBirthZodiac / viewBirthMansion）。
      this.openBirthFate(info.iso, info.dateText, info.cityText)
    },
    // [guanxing patch] M-SW15：计算出生时刻星命并弹出弹窗。iso 是带 +08:00 偏移
    // 的完整时刻（birth-overlay 提交值 / URL query.date），与引擎冻结时刻为同一
    // UTC 瞬间——太阳/月亮黄经用 astronomy-engine 就地计算，与引擎展示的天空
    // 自洽（弹窗「查看中国星宿」聚焦的宿，正是那一刻月亮所在的宿）。
    openBirthFate: function (iso, dateText, cityText) {
      try {
        const date = new Date(iso)
        const sign = this.zodiacSignOf(dateText)
        const xingci = xingciOf(Astronomy.SunPosition(date).elon)
        const mansion = lunarMansionOf(Astronomy.EclipticGeoMoon(date).lon)
        if (!sign || !xingci || !mansion) return
        this.birthFate = { dateText: dateText, cityText: cityText, sign: sign, xingci: xingci, mansion: mansion }
        // [guanxing patch] M-SW25：出生 iso 存 store——太阳系对话框打开时用它
        // 定位 ?t=（星命弹窗「你出生时的太阳系」行），不依赖引擎时钟（引擎
        // utc 会被太阳系关闭链路写回覆盖，重开弹窗必须仍是出生瞬间）。
        this.$store.commit('setValue', { varName: 'solarSystemBirthIso', newValue: iso })
        this.showFateDialog = true
      } catch (e) { console.log('Birth fate unavailable: ' + e) }
    },
    // [guanxing patch] M-SW15：弹窗「星座」行——关弹窗后聚焦出生西方星座
    // （focusZodiac 内含金黄高亮 + 定框 + 地景淡化，M-SW5/M-SW8 既有链路）。
    // [guanxing patch] M-SW53：跳转前清星命上下文——星座行知识栏走通用
    // 星座介绍，不得残留上一次星次/星君块。
    viewBirthZodiac: function () {
      this.showFateDialog = false
      this.$store.commit('setValue', { varName: 'fateView', newValue: null })
      if (this.birthFate) this.focusZodiac(this.birthFate.dateText)
    },
    // [guanxing patch] M-SW15：弹窗「本命宿」行——关弹窗后聚焦本命宿。
    // [guanxing patch] M-SW53：清星命上下文，宿行知识栏走通用宿介绍。
    viewBirthMansion: function () {
      this.showFateDialog = false
      this.$store.commit('setValue', { varName: 'fateView', newValue: null })
      if (this.birthFate) this.focusMansion(this.birthFate.mansion)
    },
    // [guanxing patch] M-SW53（用户需求「本命星君要自己独特的文化知识介绍」）：
    // 星君行独立事件——写星君上下文（{type:'lord', name, objId}）后跳同一宿；
    // 知识栏据此在宿块之前并列星君块（连线仍是该宿连线，用户定案「连线跟随对象」）。
    viewBirthLord: function () {
      this.showFateDialog = false
      if (!this.birthFate || !this.birthFate.mansion) return
      const m = this.birthFate.mansion
      this.$store.commit('setValue', { varName: 'fateView', newValue: { type: 'lord', name: m.lord, objId: m.obj } })
      this.focusMansion(m)
    },
    // [guanxing patch] M-SW19 旧行为（跳星次对应的西方星座）→ **M-SW62 替换**。
    // 用户需求：「星次展示的星星连线 要是真实的星次的星星连线 或者 虚线框包围起来」。
    // AskUserQuestion 定案：连线 + 虚线框都做 / 归属用本仓正文口径（birth-fate.js
    // XINGCI[].mansions）/ 底栏新增第五态 / 全链路收尾。
    // 新行为：切 chinese-xingci 星官文化并聚焦该星次——画面上是**该星次所含
    // 二十八宿的真实宿连线**（金黄高亮），外加环绕该星次全部星的一圈**虚线包围框**
    // （引擎对选中星官恒画 bounds，constellations.c render_bounds，无需改引擎）。
    // 知识栏仍只显示星次介绍（fateView {type:'xingci'} 上下文语义不变，仅 objId
    // 由西方星座码改为星次自身对象 id）。
    viewBirthXingci: function () {
      this.showFateDialog = false
      if (!this.birthFate || !this.birthFate.xingci) return
      const x = this.birthFate.xingci
      if (!x.obj) return
      this.$store.commit('setValue', { varName: 'fateView', newValue: { type: 'xingci', name: x.name, objId: x.obj } })
      this.focusXingci(x)
    },
    // [guanxing patch] M-SW25（用户需求「太阳系的按钮改到星命中的一栏
    // 你出生时的太阳系」）：底栏太阳系入口移除（M-SW9 入口迁入星命弹窗，
    // 底栏不再有该按钮），本行打开全屏太阳系对话框——?t= 由
    // solar-system-dialog 按 store 的 solarSystemBirthIso 定位到出生瞬间。
    // 弹窗保持打开（星命卡为导航枢纽），全屏太阳系关闭后回到本卡。
    viewBirthSolar: function () {
      this.$store.commit('setValue', { varName: 'showSolarSystemDialog', newValue: true })
    },
    onBirthSkipped: function () {
      this.showBirthOverlay = false
      // [guanxing patch] M-SW21：落地页期间引擎以 100 倍速缓慢漂移（birth-overlay
      // 的 engineReady watcher 设置），跳过后返回实时星空必须恢复真实时间流速。
      if (this.$stel) this.$stel.core.time_speed = 1
      // [guanxing patch] M-SW21b（用户需求「这个首页默认关闭大气」）：落地页期间
      // 大气被 watcher 关闭，跳过后恢复真实观感。
      if (this.$stel) this.$stel.core.atmosphere.visible = true
      // [guanxing patch]（用户需求「首页背景星空的星座名字不显示」）：跳过后
      // 恢复星座名标签（落地页期间被 watcher 隐藏，「默认打开」契约回归）。
      if (this.$stel) this.$stel.core.constellations.labels_visible = true
    },
    // [guanxing patch] M-SW5：占星十二宫判定。dateStr 取前 10 字符
    // 'YYYY-MM-DD'（birth-overlay 的 dateText 与 query.date ISO 串均为
    // +08:00 民用时日期）。摩羯座跨年段用 wrap 特判。
    zodiacSignOf: function (dateStr) {
      if (!dateStr || dateStr.length < 10) return null
      const m = Number(dateStr.slice(5, 7))
      const d = Number(dateStr.slice(8, 10))
      if (!m || !d) return null
      const md = m * 100 + d
      for (const s of ZODIAC_SIGNS) {
        const from = s.m0 * 100 + s.d0
        const to = s.m1 * 100 + s.d1
        if (from <= to ? (md >= from && md <= to) : (md >= from || md <= to)) return s
      }
      return null
    },
    // [guanxing patch] M-SW5：出生星座聚焦——切西方星座并开连线/标签
    // （金黄高亮依赖西方连线），选中对应星座（引擎对 selected 渲染金黄
    // 连线与标签，vendor constellations.c M-SW5 补丁）并转向其中心。
    // 星座对象由 skyculture 数据异步创建（birth=1 直达时 onReady 早于
    // index.json 加载完成），轮询等待对象出现（250ms × 32 ≈ 8s 上限）。
    // [guanxing patch] M-SW8：放大聚焦——选中自带的 pointAndLock 以 1s
    // 转向星座中心，同步播放等长 fov 动画（zoomTo）；目标视场按星座半径
    // 定框（2.6 倍半径留边距，夹 30°~90°；引擎 core.fov 对应窄轴，竖屏
    // 也能框住）。星座中心在地平线以下（被地景圆盘遮挡）时，放大转向的
    // 同时淡化地景：landscapes.visible=false 经 fader（0.3s）平滑淡出
    // 圆盘/地平线圈/草边，且立即解除引擎地平线下载剪
    // （is_below_horizon_hidden 读该开关，vendor core.c）。
    focusZodiac: function (dateStr) {
      const sig = this.zodiacSignOf(dateStr)
      if (!sig) return null
      return this.focusZodiacCode(sig.code)
    },
    // [guanxing patch] M-SW19：按 IAU 三字码聚焦西方星座（focusZodiac 抽出复用；
    // 弹窗「星座」行走生日码、「星次」行走星次对应星座码，同一聚焦链路）。
    // 切西方星座并开连线/标签（金黄高亮依赖西方连线），选中对应星座（引擎对
    // selected 渲染金黄连线与标签，vendor constellations.c M-SW5 补丁）并转向
    // 其中心。星座对象由 skyculture 数据异步创建（birth=1 直达时 onReady 早于
    // index.json 加载完成），轮询等待对象出现（250ms × 32 ≈ 8s 上限）。
    // [guanxing patch] M-SW8：放大聚焦——选中自带的 pointAndLock 以 1s
    // 转向星座中心，同步播放等长 fov 动画（zoomTo）；目标视场按星座半径
    // 定框（2.6 倍半径留边距，夹 30°~90°；引擎 core.fov 对应窄轴，竖屏
    // 也能框住）。星座中心在地平线以下（被地景圆盘遮挡）时，放大转向的
    // 同时淡化地景：landscapes.visible=false 经 fader（0.3s）平滑淡出
    // 圆盘/地平线圈/草边，且立即解除引擎地平线下载剪
    // （is_below_horizon_hidden 读该开关，vendor core.c）。
    focusZodiacCode: function (code) {
      const DD2R = Math.PI / 180
      // [guanxing patch] M-SW56：由 IAU 三字码反查中文名——星座行（生日码）与
      // 星次行（映射码）都走本函数，中文名仅用于遮挡提示主句（查不到时退回码）。
      const zsig = ZODIAC_SIGNS.find((z) => z.code === code)
      const zhName = (zsig && zsig.zh) || code
      this.$store.commit('setValue', { varName: 'skycultureId', newValue: 'western' })
      this.$stel.core.skycultures.current_id = 'western'
      this.$stel.core.constellations.lines_visible = true
      this.$stel.core.constellations.labels_visible = true
      const pick = () => {
        const obj = this.$stel.getObj('CON western ' + code)
        if (!obj) return false
        // M-SW8：radius 就绪 = 星座首次 update 完成（getInfo 对未就绪对象
        // 返回 undefined），此时 pvo 才有效、pointAndLock 才不会指向空向量。
        const radius = obj.getInfo('radius')
        if (!radius) return false
        // M-SW8：目标视场按星座半径定框，与 1s 转向同步播放。
        const fov = Math.min(90 * DD2R, Math.max(30 * DD2R, 2.6 * radius))
        this.$stel.zoomTo(fov, 1.0)
        // M-SW8：星座中心地平坐标高度 < 0（在地景下）→ 淡化地景露出星座。
        const pvo = obj.getInfo('pvo')
        if (pvo && pvo[0]) {
          const dir = this.$stel.convertFrame(this.$stel.observer, 'ICRF', 'OBSERVED', pvo[0])
          const alt = this.$stel.anpm(this.$stel.c2s(dir)[1])
          if (alt < 0) {
            // M-SW26（用户反馈「填完信息之后 进入后 默认开启地景」，AskUserQuestion
            // 定案「短暂让路后自动恢复」）：淡出地景露出地平线下的星座/宿
            // （M-SW8 设计），1.6s 后（zoomTo 1s 动画 + fader 0.3s + 余量）
            // 自动恢复开启——跳转后回到主界面时地景始终默认开，聚焦瞬间
            // 保留完整星座。clearTimeout 防连续跳转重复恢复。
            // [guanxing patch] M-SW36（用户需求「聚焦在星座上时地景颜色更淡一点」）：
            // 由「完全隐藏」改为「调淡让路」——地景半透明可见（引擎补的
            // landscapes.opacity）并同时解除地平线下载剪。见 setLandscapeDim。
            this.setLandscapeDim(true)
            this.$_landOffByFocus = true
            clearTimeout(this.$_landRestoreTimer)
            this.$_landRestoreTimer = setTimeout(() => {
              if (this.$_landOffByFocus) {
                this.$_landOffByFocus = false
                if (this.$stel) this.setLandscapeDim(false)
              }
            }, 1600)
            // [guanxing patch] M-SW56：目标被地景遮挡（alt<0）→ 锁定后弹居中
            // 提示（地景同时处于开启状态时才弹，见 scheduleLockHint）。
            this.scheduleLockHint(zhName)
          }
        }
        swh.setSweObjAsSelection(obj)
        return true
      }
      if (!pick()) {
        let tries = 0
        const timer = setInterval(() => {
          tries += 1
          if (pick() || tries > 32) clearInterval(timer)
        }, 250)
      }
      return code
    },
    // [guanxing patch] M-SW15：本命宿聚焦（弹窗「本命宿」/「本命星君」行点击）。与
    // focusZodiac 同一套路：切星官文化（chinese-28，二十八宿连线 + 宿名标签；
    // store 镜像同步，底栏四态循环按钮状态随之派生）→ 轮询等宿对象 radius 就绪
    // （skyculture 数据异步创建，getInfo 对未就绪对象返回 undefined）→
    // pointAndLock 转向 + zoomTo 按 2.6 倍半径定框夹 30°~90° → 宿中心在地平线
    // 以下时淡出地景（landscapes.visible=false，fader 平滑 + 解除地平线下载剪）。
    // 选中宿后信息卡联动 M-SW11 的逐宿古画知识块。
    focusMansion: function (mansion) {
      if (!mansion) return
      const DD2R = Math.PI / 180
      this.$store.commit('setValue', { varName: 'skycultureId', newValue: 'chinese-28' })
      this.$stel.core.skycultures.current_id = 'chinese-28'
      this.$stel.core.constellations.lines_visible = true
      this.$stel.core.constellations.labels_visible = true
      const pick = () => {
        const obj = this.$stel.getObj(mansion.obj)
        if (!obj) return false
        // radius 就绪 = 宿首次 update 完成，pvo/pointAndLock 才有效
        const radius = obj.getInfo('radius')
        if (!radius) return false
        const fov = Math.min(90 * DD2R, Math.max(30 * DD2R, 2.6 * radius))
        this.$stel.zoomTo(fov, 1.0)
        const pvo = obj.getInfo('pvo')
        if (pvo && pvo[0]) {
          const dir = this.$stel.convertFrame(this.$stel.observer, 'ICRF', 'OBSERVED', pvo[0])
          const alt = this.$stel.anpm(this.$stel.c2s(dir)[1])
          if (alt < 0) {
            // M-SW26（用户反馈「填完信息之后 进入后 默认开启地景」，AskUserQuestion
            // 定案「短暂让路后自动恢复」）：淡出地景露出地平线下的星座/宿
            // （M-SW8 设计），1.6s 后（zoomTo 1s 动画 + fader 0.3s + 余量）
            // 自动恢复开启——跳转后回到主界面时地景始终默认开，聚焦瞬间
            // 保留完整星座。clearTimeout 防连续跳转重复恢复。
            // [guanxing patch] M-SW36（用户需求「聚焦在星座上时地景颜色更淡一点」）：
            // 由「完全隐藏」改为「调淡让路」——地景半透明可见（引擎补的
            // landscapes.opacity）并同时解除地平线下载剪。见 setLandscapeDim。
            this.setLandscapeDim(true)
            this.$_landOffByFocus = true
            clearTimeout(this.$_landRestoreTimer)
            this.$_landRestoreTimer = setTimeout(() => {
              if (this.$_landOffByFocus) {
                this.$_landOffByFocus = false
                if (this.$stel) this.setLandscapeDim(false)
              }
            }, 1600)
            // [guanxing patch] M-SW56：本命宿被地景遮挡（alt<0）→ 锁定后弹居中
            // 提示（地景开启时；宿名统一加「宿」字，如「角宿」）。
            this.scheduleLockHint(mansion.name + '宿')
          }
        }
        swh.setSweObjAsSelection(obj)
        return true
      }
      if (!pick()) {
        let tries = 0
        const timer = setInterval(() => {
          tries += 1
          if (pick() || tries > 32) clearInterval(timer)
        }, 250)
      }
    },
    // [guanxing patch] M-SW62：星次聚焦（弹窗「星次」行点击）。与 focusMansion
    // 同一套路，只是切到 chinese-xingci 星官文化（十二星次：真实宿连线 + 选中态
    // 自带虚线包围框）→ 轮询等星次对象 radius 就绪（skyculture 数据异步创建）→
    // pointAndLock 转向 + zoomTo 按 2.6 倍半径定框夹 30°~90° → 星次中心在地平线
    // 以下时调淡地景让路（setLandscapeDim，1.6s 后自动恢复）并弹居中轻提示。
    // 选中该星次后引擎自动画：金黄星次连线（constellations.c M-SW5 选中高亮）
    // + 浅粉虚线包围框（同文件 render_bounds 对 selected 恒画，与全局
    // bounds_visible 无关）——故「聚焦某星次即出框」，引擎零改动。
    focusXingci: function (xingci) {
      if (!xingci || !xingci.obj) return
      const DD2R = Math.PI / 180
      this.$store.commit('setValue', { varName: 'skycultureId', newValue: 'chinese-xingci' })
      this.$stel.core.skycultures.current_id = 'chinese-xingci'
      this.$stel.core.constellations.lines_visible = true
      this.$stel.core.constellations.labels_visible = true
      const pick = () => {
        const obj = this.$stel.getObj(xingci.obj)
        if (!obj) return false
        // radius 就绪 = 星次首次 update 完成，pvo/pointAndLock 才有效
        const radius = obj.getInfo('radius')
        if (!radius) return false
        const fov = Math.min(90 * DD2R, Math.max(30 * DD2R, 2.6 * radius))
        this.$stel.zoomTo(fov, 1.0)
        const pvo = obj.getInfo('pvo')
        if (pvo && pvo[0]) {
          const dir = this.$stel.convertFrame(this.$stel.observer, 'ICRF', 'OBSERVED', pvo[0])
          const alt = this.$stel.anpm(this.$stel.c2s(dir)[1])
          if (alt < 0) {
            this.setLandscapeDim(true)
            this.$_landOffByFocus = true
            clearTimeout(this.$_landRestoreTimer)
            this.$_landRestoreTimer = setTimeout(() => {
              if (this.$_landOffByFocus) {
                this.$_landOffByFocus = false
                if (this.$stel) this.setLandscapeDim(false)
              }
            }, 1600)
            // 提示主句用星次名（「镜头已落在「星纪」上」，语义自足，不加「次」字）
            this.scheduleLockHint(xingci.name)
          }
        }
        swh.setSweObjAsSelection(obj)
        return true
      }
      if (!pick()) {
        let tries = 0
        const timer = setInterval(() => {
          tries += 1
          if (pick() || tries > 32) clearInterval(timer)
        }, 250)
      }
    },
    // [guanxing patch] M-SW56（用户需求「点击星命里的按钮后 锁定到星座上之后
    // 2 秒后 屏幕中央弹出一个提示 淡入淡出 如果被地景遮挡 下方 视图设置>关闭
    // 地景 显示 3 秒」）：锁定成功后于屏幕中央弹出的轻提示。
    // 时序：本方法在 pick() 成功、zoomTo 已启动之后被调用 → 等 1.0s（转向
    // 动画）+ 2.0s（决策人定案「锁定动画后再等 2 秒」）= 调用后 3s 弹出；
    // 可见期 5s（M-SW56b：用户「提示时长 延长 2 秒」，由 3s 延长为 5s；另有
    // gx-hint 过渡前后各 0.3s 淡入淡出）。
    // 触发前提（决策人 2026-10-14 定案）：仅「被地景遮挡 + 地景处于开启状态」
    // 时提示；未遮挡、或用户已在「视图设置」里关闭地景
    // （core.landscapes.visible === false）则不提示。调用方（focusZodiacCode /
    // focusMansion 的 alt<0 分支）已保证遮挡成立，这里只补判地景开关。
    // 连续跳转用 clearTimeout 重置计时，避免上一次的计时器提前熄灭新提示。
    scheduleLockHint: function (name) {
      const core = this.$stel && this.$stel.core
      if (!core || !core.landscapes || core.landscapes.visible === false) return
      clearTimeout(this.$_lockHintShowTimer)
      clearTimeout(this.$_lockHintHideTimer)
      this.lockHint = false
      // 3000ms = 1000（zoomTo 转向动画）+ 2000（锁定完成后再等 2s）。
      this.$_lockHintShowTimer = setTimeout(() => {
        this.lockHintName = name
        this.lockHint = true
        clearTimeout(this.$_lockHintHideTimer)
        // 可见期 5s（M-SW56b：由 3s 延长 2s），随后 v-if 置 false 触发 gx-hint 淡出。
        this.$_lockHintHideTimer = setTimeout(() => { this.lockHint = false }, 5000)
      }, 3000)
    },
    // [guanxing patch] M-SW36（用户需求「聚焦在星座上的时候 地景的颜色 更淡一点」）：
    // 引擎原有的 landscapes.visible 是布尔量（fader 只能在 0/1 两端），只能「全显示 /
    // 全隐藏」——聚焦地平线下的星座时，要么星座被地景挡掉、要么地景整个消失。M-SW36
    // 给 vendor 引擎补了两样东西（见 landscape.c / core.c）：
    //   · landscapes.opacity（0..1 连续量）—— 渲染时乘进 painter.color[3]；
    //   · landscapes.hide_below_horizon（布尔）—— core 的地平线裁剪改读它，不再
    //     绑死在 visible 上（旧实现靠 visible=false 顺带解除裁剪）。
    // 本方法统一开关：on=true 调淡到 LAND_DIM_OPACITY 并解除裁剪，让地平线下的
    // 星座露出来；on=false 回到完全不透明 + 恢复裁剪。
    // 降级：若引擎产物不含 opacity 属性（例如回滚到重编译前的旧 wasm），退回原来的
    // visible 布尔开关，保证聚焦功能不因引擎版本而失效。
    setLandscapeDim: function (on) {
      const core = this.$stel && this.$stel.core
      if (!core || !core.landscapes) return
      const ls = core.landscapes
      if (!('opacity' in ls)) {
        ls.visible = !on // 降级路径：旧引擎只有布尔量
        return
      }
      ls.hide_below_horizon = !on
      this.$_fadeLandOpacity(on ? LAND_DIM_OPACITY : 1)
    },
    // M-SW36：地景不透明度平滑过渡（引擎侧是硬值，直接改会有一帧跳变；
    // 聚焦时正播着 1s 的 zoomTo 动画，300ms 渐变与之叠加最自然）。
    $_fadeLandOpacity: function (to) {
      const from = typeof this.$_landOpacity === 'number' ? this.$_landOpacity : 1
      if (Math.abs(to - from) < 0.001) {
        if (this.$stel) this.$stel.core.landscapes.opacity = to
        this.$_landOpacity = to
        return
      }
      if (this.$_landRaf) cancelAnimationFrame(this.$_landRaf)
      const t0 = performance.now()
      const DUR = 300
      const step = (t) => {
        const k = Math.min(1, (t - t0) / DUR)
        const v = from + (to - from) * k
        if (this.$stel) this.$stel.core.landscapes.opacity = v
        this.$_landOpacity = v
        this.$_landRaf = k < 1 ? requestAnimationFrame(step) : 0
      }
      this.$_landRaf = requestAnimationFrame(step)
    },
    getStoreValue: function (storeVarName) {
      return _.get(this.$store.state, storeVarName)
    },
    setStateFromQueryArgs: function () {
      // Check whether the observing panel must be displayed
      this.$store.commit('setValue', { varName: 'showSidePanel', newValue: this.$route.path.startsWith('/p/') })

      // Set the core's state from URL query arguments such
      // as date, location, view direction & fov
      var that = this

      if (!this.initDone) {
        this.$stel.core.time_speed = 1
        let d = new Date()
        if (this.$route.query.date) {
          d = new Moment(this.$route.query.date).toDate()
          this.$stel.core.observer.utc = d.getMJD()
          this.startTimeIsSet = true
          // [guanxing patch] birth=1（M-SW4，出生天象链接）：直达也冻结在该时刻。
          // 不带 birth 的既有分享链接保持「从该时刻开始流动」的上游语义。
          if (this.$route.query.birth) {
            this.$stel.core.time_speed = 0
            // [guanxing patch] M-SW15：birth=1 直达同样弹本命星命弹窗（分享链接/
            // 刷新重现路径，与提交路径同款；聚焦动作交给弹窗按钮）。
            const q = this.$route.query
            that.openBirthFate(q.date, String(q.date).replace('T', ' '), q.city || '')
          }
        }

        if (this.$route.query.lng && this.$route.query.lat) {
          const pos = { lat: Number(this.$route.query.lat), lng: Number(this.$route.query.lng), alt: this.$route.query.elev ? Number(this.$route.query.elev) : 0, accuracy: 1 }
          swh.geoCodePosition(pos, that).then((loc) => {
            // [guanxing patch] M-SW4：出生天象链接带 ?city= 中文名，覆盖
            // geoCodePosition 的「Lat x° Lon y°」英文显示，保持界面简体中文。
            if (that.$route.query.city) {
              loc.short_name = that.$route.query.city
            }
            that.$store.commit('setCurrentLocation', loc)
          }, (error) => { console.log(error) })
        }

        this.$stel.core.observer.yaw = this.$route.query.az ? Number(this.$route.query.az) * Math.PI / 180 : 0
        this.$stel.core.observer.pitch = this.$route.query.alt ? Number(this.$route.query.alt) * Math.PI / 180 : 30 * Math.PI / 180
        this.$stel.core.fov = this.$route.query.fov ? Number(this.$route.query.fov) * Math.PI / 180 : 120 * Math.PI / 180

        this.initDone = true
      }

      if (this.$route.path.startsWith('/skysource/')) {
        const name = decodeURIComponent(this.$route.path.substring(11))
        console.log('Will select object: ' + name)
        return swh.lookupSkySourceByName(name).then(ss => {
          if (!ss) {
            return
          }
          let obj = swh.skySource2SweObj(ss)
          if (!obj) {
            obj = this.$stel.createObj(ss.model, ss)
            this.$selectionLayer.add(obj)
          }
          if (!obj) {
            console.warning("Can't find object in SWE: " + ss.names[0])
          }
          swh.setSweObjAsSelection(obj)
        }, err => {
          console.log(err)
          console.log("Couldn't find skysource for name: " + name)
        })
      }
    }
  },
  computed: {
    nav: {
      get: function () {
        return this.$store.state.showNavigationDrawer
      },
      set: function (v) {
        if (this.$store.state.showNavigationDrawer !== v) {
          this.$store.commit('toggleBool', 'showNavigationDrawer')
        }
      }
    },
    storeCurrentLocation: function () {
      return this.$store.state.currentLocation
    }
  },
  watch: {
    // [guanxing patch] 落地页隐藏底栏（M-SW21c，用户反馈「首页是隐藏 下面的
    // 横栏」）：showBirthOverlay 镜像进 store（landingActive），gui.vue 的
    // bottom-bar 以 ui-hidden 复用淡出机制整条隐藏；immediate 覆盖首访直挂
    // 与 birth=1 直达不挂两种初值，离开路径（提交/跳过置 false）自动恢复。
    showBirthOverlay: {
      handler: function (v) {
        this.$store.commit('setValue', { varName: 'landingActive', newValue: v })
      },
      immediate: true
    },
    storeCurrentLocation: function (loc) {
      const DD2R = Math.PI / 180
      this.$stel.core.observer.latitude = loc.lat * DD2R
      this.$stel.core.observer.longitude = loc.lng * DD2R
      this.$stel.core.observer.elevation = loc.alt

      // At startup, we need to wait for the location to be set before deciding which
      // startup time to set so that it's night time.
      if (!this.startTimeIsSet) {
        this.$stel.core.observer.utc = swh.getTimeAfterSunset(this.$stel)
        this.startTimeIsSet = true
      }
      // Init of time and date is complete
      this.$store.commit('setValue', { varName: 'initComplete', newValue: true })
    },
    $route: function () {
      // react to route changes...
      this.setStateFromQueryArgs()
    }
  },
  mounted: function () {
    var that = this

    for (const i in this.$stellariumWebPlugins()) {
      const plugin = this.$stellariumWebPlugins()[i]
      if (plugin.onAppMounted) {
        plugin.onAppMounted(that)
      }
    }

    import('@/assets/js/stellarium-web-engine.wasm').then(f => {
      // Initialize the StelWebEngine viewer singleton
      // After this call, the StelWebEngine state will always be available in vuex store
      // in the $store.stel object in a reactive way (useful for vue components).
      // To modify the state of the StelWebEngine, it's enough to call/set values directly on the $stel object
      try {
        swh.initStelWebEngine(that.$store, f.default, that.$refs.stelCanvas, function () {
          // [guanxing patch] 先把默认观测点（北京）写入引擎 observer：currentLocation
          // 的初始值不会触发 watcher，不主动提交一次引擎会停在出厂位置。用 setValue
          // 换引用触发 watcher（setCurrentLocation 会置 useAutoLocation=false，导致
          // 其后自动定位不再生效）。初始时刻由 watcher 里的 getTimeAfterSunset 决定。
          that.$store.commit('setValue', { varName: 'currentLocation', newValue: Object.assign({}, that.$store.state.currentLocation) })
          // Start auto location detection（M-SW3c：失败不猜测位置，保持北京默认）
          swh.getGeolocation().then(p => swh.geoCodePosition(p, that)).then((loc) => {
            that.$store.commit('setAutoDetectedLocation', loc)
          }, (error) => { console.log('Geolocation unavailable, fallback to default Beijing: ' + error) })

          // [guanxing patch] 引擎画布标签字体换成含 CJK 字形的 Noto Sans SC
          // 子集（pipeline/build-font-subset.mjs 生成），否则星座/星名中文
          // 会渲染成豆腐块。
          that.$stel.setFont('regular', process.env.BASE_URL + 'fonts/GuanxingSC-Regular.otf', 1.38)
          that.$stel.setFont('bold', process.env.BASE_URL + 'fonts/GuanxingSC-Bold.otf', 1.38)
          that.$stel.core.constellations.show_only_pointed = false
          // [guanxing patch] 星座连线与星名标签默认打开（观星的核心卖点；
          // 上游默认关闭，靠底部栏按钮切换）。
          that.$stel.core.constellations.lines_visible = true
          that.$stel.core.constellations.labels_visible = true
          // [guanxing patch] M-SW32：星座艺术图默认打开（用户需求「默认打开星座
          // 艺术图」，AskUserQuestion 定案「全端都默认开 + 保留开关可手动关」）。
          // 引擎初值 false（vendor/src/modules/constellations.c fader_init），
          // 这里 onReady 直写引擎属性；「图层」菜单开关仍在（bottom-bar.vue），
          // 用户可自行关闭。艺术图在广角可见、放大到该星座时由引擎自动淡出
          // （constellations.c render_img 的 smoothstep），无需重编引擎。
          that.$stel.core.constellations.images_visible = true

          that.setStateFromQueryArgs()
          that.guiComponent = 'Gui'
          for (const i in that.$stellariumWebPlugins()) {
            const plugin = that.$stellariumWebPlugins()[i]
            if (plugin.onEngineReady) {
              plugin.onEngineReady(that)
            }
          }

          if (!that.dataSourceInitDone) {
            // Set all default data sources
            const core = that.$stel.core
            core.stars.addDataSource({ url: process.env.BASE_URL + 'skydata/stars' })

            // Allow to specify a custom path for sky culture data
            // [guanxing patch] 默认同时注册 western 与 chinese（中国星官，M-SW3b），
            // 可用底栏按钮切换；?sc= 指定自定义路径时只注册该源（保持上游行为）。
            if (that.$route.query.sc) {
              const key = that.$route.query.sc.substring(that.$route.query.sc.lastIndexOf('/') + 1)
              core.skycultures.addDataSource({ url: that.$route.query.sc, key: key })
              core.skycultures.current_id = key
              that.$store.commit('setValue', { varName: 'skycultureId', newValue: key })
            } else {
              core.skycultures.addDataSource({ url: process.env.BASE_URL + 'skydata/skycultures/western', key: 'western' })
              core.skycultures.addDataSource({ url: process.env.BASE_URL + 'skydata/skycultures/chinese', key: 'chinese' })
              // [guanxing patch] 二十八宿子集星官（M-SW4b，用户需求）：只含 28 宿，
              // 供「图层」菜单「二十八宿」开关切换（bottom-bar.vue）。
              core.skycultures.addDataSource({ url: process.env.BASE_URL + 'skydata/skycultures/chinese-28', key: 'chinese-28' })
              // [guanxing patch] M-SW62（用户需求「星次展示的星星连线 要是真实的
              // 星次的星星连线 或者 虚线框包围起来」）：十二星次星官文化（12 次，
              // 连线 = 所含 28 宿连线拼接，edges = 所含星凸包虚线框），供底栏
              // 五态循环的「十二星次」一态与星命弹窗「星次」行聚焦使用。
              core.skycultures.addDataSource({ url: process.env.BASE_URL + 'skydata/skycultures/chinese-xingci', key: 'chinese-xingci' })
              core.skycultures.current_id = 'western'
            }

            core.dsos.addDataSource({ url: process.env.BASE_URL + 'skydata/dso' })
            // [guanxing patch] M-SW15（用户需求）：深空天体默认关闭——引擎
            // fader_init 默认 true，这里 onReady 直写引擎属性；底栏按钮同步移除。
            core.dsos.visible = false
            // [guanxing patch] 地景直接用引擎内置纯地平线（zero landscape）：
            // key="zero" 时引擎不加载 hips，画深绿圆盘 + 绿色地平线圈
            // （vendor/src/core/landscape.c），不再请求 skydata/landscapes。
            core.landscapes.addDataSource({ url: '', key: 'zero' })
            core.milkyway.addDataSource({ url: process.env.BASE_URL + 'skydata/surveys/milkyway' })
            core.minor_planets.addDataSource({ url: process.env.BASE_URL + 'skydata/mpcorb.dat', key: 'mpc_asteroids' })
            core.planets.addDataSource({ url: process.env.BASE_URL + 'skydata/surveys/sso/moon', key: 'moon' })
            core.planets.addDataSource({ url: process.env.BASE_URL + 'skydata/surveys/sso/sun', key: 'sun' })
            core.planets.addDataSource({ url: process.env.BASE_URL + 'skydata/surveys/sso/moon', key: 'default' })
            core.comets.addDataSource({ url: process.env.BASE_URL + 'skydata/CometEls.txt', key: 'mpc_comets' })
            core.satellites.addDataSource({ url: process.env.BASE_URL + 'skydata/tle_satellite.jsonl.gz', key: 'jsonl/sat' })
          }
        })
      } catch (e) {
        this.$store.commit('setValue', { varName: 'wasmSupport', newValue: false })
      }
    })
  }
}
</script>

<style>

/* [guanxing patch] M-SW12 两主界面 UI 统一：设计 token（原深空蓝黑毛玻璃，
   M-SW24 起完全转换为极简单色：近黑实底 + 白灰层级 + 纯白唯一强调，无毛玻璃）。
   token 名沿用旧命名（--gx-blue/--gx-gold 现为白/浅灰白，仅作历史名保留）。
   与 public/solar/styles.css 头部 :root 同名同值（solar 侧为本 token 的镜像）。 */
:root {
  --gx-panel-bg: rgba(10, 10, 12, 0.88);
  --gx-panel-border: rgba(255, 255, 255, 0.14);
  --gx-radius-panel: 10px;
  --gx-radius-ctrl: 6px;
  --gx-text: #f2f2f2;
  --gx-text-dim: rgba(242, 242, 242, 0.6);
  --gx-blue: #ffffff;
  --gx-blue-soft: rgba(255, 255, 255, 0.1);
  --gx-gold: #dedede;
  --gx-blur: 0px;
  --gx-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  --gx-font: 13px/1.5 -apple-system, "Segoe UI", system-ui, sans-serif;
}

a {
  color: var(--gx-blue);
}

a:link {
  text-decoration-line: none;
}

.divider_menu {
  margin-top: 8px;
  margin-bottom: 8px;
}

html {
  overflow-y: visible;
}

html, body, #app {
  overflow-y: visible!important;
  overflow-x: visible;
  /* [guanxing patch] M-SW26：移动端去掉下拉刷新/越界回弹，避免拖拽星空时页面跟着动。 */
  overscroll-behavior: none;
  position: fixed!important;
  width: 100%;
  height: 100%;
  padding: 0!important;
  font-size: 14px;
}

/* [guanxing patch] M-SW12：全局字体对齐太阳系面板的 system-ui 栈（含 Vuetify 应用壳）。 */
html, body, #app, .v-application {
  font-family: -apple-system, "Segoe UI", system-ui, sans-serif!important;
}

.fullscreen {
  overflow-y: hidden;
  position: fixed;
  width: 100%;
  height: 100%;
  padding: 0!important;
}

.click-through {
  pointer-events: none;
}

.get-click {
  pointer-events: all;
}

.dialog {
  background: transparent;
}

/* [guanxing patch] M-SW12：上游选择器 .menu__content 在 Vuetify 2 实际类为
   v-menu__content（前缀 v-，原规则从未命中）；修正选择器并去掉容器自带投影，
   面板底/边/圆角统一交给下方 .v-menu__content .v-list 规则。
   特异度注意：Vuetify 2 主题类生成 .v-application .secondary{...!important}
   且运行时注入顺序在应用样式之后，同特异度打平会压过 token——凡与 Vuetify
   主题类竞争的统一加 .v-application 前缀（0,3,0）取胜。 */
.v-application .v-menu__content {
  background-color: transparent!important;
  box-shadow: none!important;
}

/* [guanxing patch] 出生天象信息条（M-SW4 / M-SW44）：fixed 浮在星空上方居中。
   M-SW44：改「深色半透明」（用户需求）。原 `.birth-banner` 已写深色底，但
   Vuetify 的主题色类（type="info" → .info，蓝底 #2196F3）是在运行时注入
   <head> 的，文档序晚于本项目 css；两者特异度同为 (0,1,0)、都带 !important
   时按文档序它取胜——这就是「设了深色还是蓝的」的根因。故选择器加
   .v-application 前缀提到 (0,2,0) 取胜（与上方 .v-menu__content、
   .v-dialog .v-card 同一套策略），并已同时去掉 v-alert 的 type="info"。
   底栏/面板同一材质语言：近黑半透明 + 白字（M-SW24 起无毛玻璃，不加 blur）。 */
.v-application .birth-banner {
  position: fixed;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 200;
  max-width: calc(100vw - 24px);
  background-color: rgba(10, 10, 12, 0.72)!important;
  border: 1px solid var(--gx-panel-border);
  border-radius: var(--gx-radius-panel);
  color: var(--gx-text)!important;
}
/* 关闭按钮：随信息条转浅色（默认色随主题，深底上会偏暗） */
.v-application .birth-banner .v-alert__dismissible,
.v-application .birth-banner .v-alert__dismissible .v-icon {
  color: var(--gx-text-dim)!important;
}
.v-application .birth-banner .v-alert__dismissible:hover,
.v-application .birth-banner .v-alert__dismissible:hover .v-icon {
  color: var(--gx-text)!important;
}

/* [guanxing patch] M-SW15b：左上角星命入口（用户需求）：随时重开本命星命
   弹窗。与 M-SW12 面板同材质，M-SW24 单色化为白字细白边；fixed 不随 uiIdle 隐藏。 */
.fate-entry {
  position: fixed;
  top: 10px;
  left: 10px;
  z-index: 200;
  padding: 5px 14px;
  background: var(--gx-panel-bg);
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 999px;
  color: var(--gx-text);
  font-size: 13px;
  line-height: 1.6;
  box-shadow: var(--gx-shadow);
  cursor: pointer;
}
.fate-entry:hover { filter: brightness(1.2); }

/* [guanxing patch] M-SW56：锁定后居中轻提示（用户需求「屏幕中央弹出一个提示
   淡入淡出 … 下方 视图设置>关闭地景 显示 3 秒」）。材质与 .birth-banner /
   .fate-entry 同一套：近黑半透明 + 白字 + 细白边 + 面板圆角。fixed 居中
   （translate -50%/-50%），pointer-events:none 保证纯展示不挡星空操作。
   z-index 200 与信息条同级、低于 v-dialog（202），弹窗关闭后才出现不冲突。 */
.lock-hint {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 200;
  pointer-events: none;
  max-width: calc(100vw - 48px);
  padding: 14px 22px;
  background: rgba(10, 10, 12, 0.72);
  border: 1px solid var(--gx-panel-border);
  border-radius: var(--gx-radius-panel);
  box-shadow: var(--gx-shadow);
  text-align: center;
}

.lock-hint-main {
  font-size: 16px;
  color: var(--gx-text);
  line-height: 1.5;
}

.lock-hint-sub {
  font-size: 12px;
  color: var(--gx-text-dim);
  margin-top: 6px;
  line-height: 1.5;
}

/* M-SW56：淡入淡出（0.3s），只过渡 opacity，不覆盖 .lock-hint 的居中 transform。 */
.gx-hint-enter-active,
.gx-hint-leave-active {
  transition: opacity 0.3s ease;
}

.gx-hint-enter,
.gx-hint-leave-to {
  opacity: 0;
}

/* [guanxing patch] M-SW21：首页落地层淡出离场（Vue transition name=gx-fade）。
   leave-active 期间 pointer-events:none，防止淡出的遮罩挡住提交后同步弹出的
   本命星命弹窗的点击。 */
.gx-fade-enter-active,
.gx-fade-leave-active {
  transition: opacity 0.6s ease;
}

.gx-fade-enter,
.gx-fade-leave-to {
  opacity: 0;
}

.gx-fade-leave-active {
  pointer-events: none;
}

/* [guanxing patch] M-SW12：弹出层统一皮肤——底栏三个 v-menu 的 v-list 与
   各 v-dialog 的 v-card 走同一套面板（材质与左上主面板/太阳系面板一致），
   M-SW24 起为近黑实底无毛玻璃。 */
.v-application .v-menu__content .v-list {
  background: var(--gx-panel-bg)!important;
  border: 1px solid var(--gx-panel-border)!important;
  border-radius: var(--gx-radius-panel)!important;
  box-shadow: var(--gx-shadow)!important;
  overflow: hidden;
}

.v-application .v-dialog .v-card {
  background: var(--gx-panel-bg)!important;
  border: 1px solid var(--gx-panel-border)!important;
  border-radius: var(--gx-radius-panel)!important;
  box-shadow: var(--gx-shadow)!important;
  color: var(--gx-text);
}

/* [guanxing patch] M-SW24b：全局滚动条单色化（用户反馈「拖动条改成 颜色一致的」）——
   Windows 默认浅灰滚动条在近黑单色主题上突兀；全站统一白灰细滚动条：
   8px 白 0.25 thumb（与信息卡既有先例同值）+ hover 0.35 + 透明轨道，
   覆盖落地页内部滚动/底栏城市列表/时分下拉菜单/各弹窗/信息卡。 */
::-webkit-scrollbar { width: 8px; height: 8px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.25); border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.35); }

#stel {height: 100%; width: 100%; position: absolute;}
/* [guanxing patch] M-SW26：移动端触屏适配——touch-action:none 让浏览器不再把画布上的
   单指手势当页面滚动、双指当页面缩放，星空拖拽/双指缩放即时响应（也去掉双击缩放延迟）。
   与 solar 子应用既有做法一致；UI 面板在画布之上，不受影响。 */
#stel-canvas {z-index: -10; width: 100%; height: 100%; touch-action: none;}

.right_panel {
  padding-right: 400px;
}

.v-btn {
  margin-left: 8px;
  margin-right: 8px;
  margin-top: 6px;
  margin-bottom: 6px;
}

.v-application--wrap {
  min-height: 100%!important;
}

</style>
